class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    initCartReservedTimer();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    startCartReservedTimer();
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      initCartReservedTimer();
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

const CART_RESERVED_DURATION_SECONDS = 10 * 60;
const CART_RESERVED_STORAGE_KEY = 'bumpCartReservedStartedAtV2';
const CART_RESERVED_TIMER_SELECTOR = '[data-cart-reserved-timer]';

function startCartReservedTimer() {
  try {
    sessionStorage.setItem(CART_RESERVED_STORAGE_KEY, String(Date.now()));
  } catch (error) {
    window.bumpCartReservedStartedAt = Date.now();
  }
}

function getCartReservedStartedAt() {
  try {
    const storedStart = Number(sessionStorage.getItem(CART_RESERVED_STORAGE_KEY));
    const elapsedSeconds = Math.floor((Date.now() - storedStart) / 1000);
    if (storedStart && elapsedSeconds < CART_RESERVED_DURATION_SECONDS) return storedStart;
  } catch (error) {
    const fallbackStart = window.bumpCartReservedStartedAt;
    const elapsedSeconds = Math.floor((Date.now() - fallbackStart) / 1000);
    if (fallbackStart && elapsedSeconds < CART_RESERVED_DURATION_SECONDS) return fallbackStart;
  }

  startCartReservedTimer();

  try {
    return Number(sessionStorage.getItem(CART_RESERVED_STORAGE_KEY));
  } catch (error) {
    return window.bumpCartReservedStartedAt;
  }
}

function formatCartReservedTime(totalSeconds) {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = String(totalSeconds % 60).padStart(2, '0');
  return `${minutes}:${seconds}`;
}

function initCartReservedTimer() {
  const timerEls = document.querySelectorAll(CART_RESERVED_TIMER_SELECTOR);
  if (!timerEls.length) {
    clearInterval(window.bumpCartReservedInterval);
    return;
  }

  const startedAt = getCartReservedStartedAt();
  clearInterval(window.bumpCartReservedInterval);

  const updateTimer = () => {
    const elapsedSeconds = Math.floor((Date.now() - startedAt) / 1000);
    const remainingSeconds = Math.max(CART_RESERVED_DURATION_SECONDS - elapsedSeconds, 0);
    const formattedTime = formatCartReservedTime(remainingSeconds);

    timerEls.forEach((timerEl) => {
      timerEl.textContent = formattedTime;
    });

    if (remainingSeconds <= 0) clearInterval(window.bumpCartReservedInterval);
  };

  updateTimer();
  window.bumpCartReservedInterval = setInterval(updateTimer, 1000);
}

function scheduleCartReservedTimerInit() {
  clearTimeout(window.bumpCartReservedInitTimeout);
  window.bumpCartReservedInitTimeout = setTimeout(initCartReservedTimer, 0);
}

document.addEventListener('DOMContentLoaded', initCartReservedTimer);
document.addEventListener('shopify:section:load', scheduleCartReservedTimerInit);
document.addEventListener('cart:updated', scheduleCartReservedTimerInit);

if (!window.bumpCartReservedObserver) {
  window.bumpCartReservedObserver = new MutationObserver((mutations) => {
    const shouldInit = mutations.some((mutation) => {
      return Array.from(mutation.addedNodes).some((node) => {
        if (!(node instanceof Element)) return false;
        return node.matches(CART_RESERVED_TIMER_SELECTOR) || node.querySelector(CART_RESERVED_TIMER_SELECTOR);
      });
    });

    if (shouldInit) scheduleCartReservedTimerInit();
  });

  window.bumpCartReservedObserver.observe(document.documentElement, {
    childList: true,
    subtree: true,
  });
}

window.initCartReservedTimer = initCartReservedTimer;

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
